import {
  type LngLat,
  type MapListenerProps,
  type MapLocationRequest,
  type Margin,
  MapListener,
} from '@logistics-frontend/blocks'
import type {Optional} from '@logistics-frontend/types'
import {isPoint} from '@yandex-taxi/react-integration-api'
import React, {useCallback, useMemo} from 'react'

import {PointPlacemark} from '../PointPlacemark/PointPlacemark'
import {useMapZoomCenterUpdate} from './hooks'
import {getPlacemarkType} from './utils'

type Props = {
  allPoints: Optional<LngLat>[]
  route: LngLat[]
  onChange: (newCoords: LngLat, index: number) => void
  isDragPointAllowed?: boolean
  onLocationRequest?: (location: MapLocationRequest) => void
  mapListenerProps?: MapListenerProps
  margin?: Margin
}

export function MapPlacemarks(props: Props): React.ReactElement {
  const {route, allPoints, isDragPointAllowed = false, onChange, onLocationRequest, margin} = props
  const [sourcePoint, ...destinationPoints] = allPoints
  const destinationPointsCount = destinationPoints.length
  const validDestinationPointsCount = destinationPoints.filter(isPoint).length

  const preparedPoints = useMemo(() => {
    return allPoints.filter(isPoint)
  }, [allPoints])

  const handleMapClick = useCallback(
    (coords: LngLat) => {
      if (sourcePoint === undefined) {
        onChange(coords, 0)
        return
      }

      let lastIndex = route.length

      if (route.length - 1 === destinationPointsCount) {
        lastIndex -= 1
      }

      onChange(coords, lastIndex)
    },
    [sourcePoint, route.length, destinationPointsCount, onChange],
  )

  useMapZoomCenterUpdate(route, onLocationRequest, margin)

  return (
    <>
      {preparedPoints.map((point, index) => {
        return (
          <PointPlacemark
            key={index}
            coordinates={point}
            draggable={isDragPointAllowed}
            onDragEnd={coordinates => onChange(coordinates, index)}
            type={getPlacemarkType(index, validDestinationPointsCount)}
          >
            {index + 1}
          </PointPlacemark>
        )
      })}
      <MapListener onClick={(_, e) => handleMapClick(e.coordinates)} />
    </>
  )
}
